import { applyTheming } from "@pandas/core-ui";
import type { SolutionConfig } from "@pandas/http-client";
import { servicesOrchestratorValues } from "@/config/services";
import { configuratorApi } from "@/services";
import type { Configuration, GlobalConfiguration } from "@/services/types";
import { useStore } from "@/store";
import type { AppConfiguration } from "@/store/slices/config";

// Configurator response = partner settings + theming + vision app config.
type PartnerConfigResponse = SolutionConfig<Configuration & GlobalConfiguration>;

// Dedupes parallel loads of the same partner (nested route loaders run concurrently).
const pendingLoads = new Map<string, Promise<AppConfiguration>>();

async function loadPartnerConfig(partner: string, locationId?: string): Promise<AppConfiguration> {
	const response: PartnerConfigResponse = await configuratorApi.getConfig(partner, locationId);
	const config: AppConfiguration = {
		...response,
		partnerSettings: response,
		theming: applyTheming(response, partner, servicesOrchestratorValues.urls.cdn),
	};

	const store = useStore.getState();
	if (response.imeiScan) {
		const { imeiScan, grading, cameraResolution, realtimeHistory } = response;
		store.setGlobalConfig({ imeiScan, grading, cameraResolution, realtimeHistory });
	}
	store.setConfig(partner, config, locationId);

	return config;
}

/**
 * Loads a partner's config once and caches it. Safe to await from parallel loaders.
 * The store short-circuit keys on partner + locationId so a later load for the same
 * partner but a different location re-fetches instead of reusing a config that was
 * resolved for another location; the in-flight dedup keys on the same pair.
 */
export async function ensurePartnerConfig(partner: string, locationId?: string): Promise<AppConfiguration> {
	const { configId, config, configLocationId } = useStore.getState();
	if (configId === partner && config && configLocationId === (locationId ?? null)) return config;

	const cacheKey = `${partner}:${locationId ?? ""}`;
	const pending = pendingLoads.get(cacheKey);
	if (pending) return pending;

	const load = loadPartnerConfig(partner, locationId);
	pendingLoads.set(cacheKey, load);
	try {
		return await load;
	} finally {
		pendingLoads.delete(cacheKey);
	}
}
